// src/pages/UniversalLedger/components/UniversalLedgerFilters.js
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";



import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import { clearSearchResults, resetLedger, setFilters } from "../AccountTransactionSlice";
import { fetchUniversalLedger, fetchUniversalLedgerAccountsBySearch } from "../AccountTransactionThunk";
import { ACCOUNT_TYPE_OPTIONS } from "./AccountTransactionConfig";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";


const { Option } = Select;

const UniversalLedgerFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters, loadingSearch } = useSelector(
    (state) => state.universalLedger
  );

  // Set default date range on mount (same as your other ledgers)
  useDefaultDateRange(setFilters, filters, "today");

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  // When account type changes: clear account selection + results + ledger data
  const handleTypeChange = (value) => {
    dispatch(
      setFilters({
        accTypeId: value,
        accId: null,
        accName: "",
      })
    );
    dispatch(resetLedger());
    dispatch(clearSearchResults());
  };

  // Account search — only fires after type is selected
  const handleAccountSearch = useCallback(
    (text) => {
      if (!filters.accTypeId) return;
      dispatch(
        fetchUniversalLedgerAccountsBySearch({
          search: text,
          accTypeId: filters.accTypeId,
        })
      );
    },
    [dispatch, filters.accTypeId]
  );

  const handleAccountSelect = (obj) => {
    if (!obj) {
      handleFilterChange("accId", null);
      handleFilterChange("accName", "");
      return;
    }
    handleFilterChange("accId", obj.sale_id);   // sale_id holds acc_id (normalized in thunk)
    handleFilterChange("accName", obj.display_text);
  };

  const handleGetReport = () => {
    if (!filters.accTypeId) {
      messageApi.warning("Please select an Account Type");
      return;
    }
    if (!filters.accId) {
      messageApi.warning("Please select an Account");
      return;
    }
    if (!filters.fromDate || !filters.toDate) {
      messageApi.warning("Please select a Date Range");
      return;
    }

    dispatch(fetchUniversalLedger({ filters })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Ledger loaded ✅");
      } else {
        messageApi.error("Failed to load ledger ❌");
      }
    });
  };

  // Placeholder text changes based on selected type
  const selectedTypeConfig = ACCOUNT_TYPE_OPTIONS.find(
    (o) => o.value === filters.accTypeId
  );
  const searchPlaceholder = filters.accTypeId
    ? selectedTypeConfig?.searchPlaceholder || "Search account..."
    : "Select type first";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} align="middle">

        {/* Step 1 — Account Type */}
        <Col xs={24} sm={12} md={8} lg={5}>
          <label style={{ display: "block", marginBottom: 4 }}>
            Account Type
          </label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Account Type"
            value={filters.accTypeId || undefined}
            onChange={handleTypeChange}
            showSearch
            optionFilterProp="children"
            allowClear
            onClear={() => handleTypeChange(null)}
          >
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Step 2 — Account Search (always shown, disabled until type chosen) */}
        <Col xs={24} sm={12} md={8} lg={5}>
          <label style={{ display: "block", marginBottom: 4 }}>
            {selectedTypeConfig?.label || "Account"}
          </label>
          <SearchListAutoComplete
            key={filters.accTypeId || "no-type"}  // remounts when type changes, clearing old value
            entityType="universal_account"
            searchThunk={handleAccountSearch}
            resultSelector={(state) => state.universalLedger.searchResults}
            loading={loadingSearch}
            onSelect={handleAccountSelect}
            placeholder={searchPlaceholder}
            disabled={!filters.accTypeId}
            showAddNew={false}
          />
        </Col>

        {/* Date Range — reuses your CommonDateRangeFilter */}
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        {/* Get Report Button */}
        <Col
          xs={24}
          sm={12}
          md={6}
          lg={3}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleGetReport}
            block
            style={{ height: 35.19 }}
            data-testid="universal-ledger-report-btn"
          >
            Get Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default UniversalLedgerFilters;